const toCurrencyNumber = (value) => {
  const parsed = Number(String(value ?? 0).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const money = (value) =>
  `₹ ${toCurrencyNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export const text = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

export const firstValue = (row = {}, keys = [], fallback = "-") => {
  const foundKey = keys.find((key) => row[key] !== undefined && row[key] !== null && row[key] !== "");
  return foundKey ? row[foundKey] : fallback;
};

export const initials = (name = "") =>
  String(name || "ZA")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "ZA";

export const statusTone = (value) => {
  const status = String(value || "").toLowerCase();
  if (status.includes("cancel") || status.includes("reject") || status.includes("expired") || status.includes("return") || status.includes("stop")) {
    return "overdue";
  }
  if (status.includes("paid") || status.includes("active") || status.includes("accepted") || status.includes("received") || status.includes("delivered") || status.includes("confirmed") || status.includes("applied")) {
    return "paid";
  }
  if (status.includes("draft")) return "draft";
  return "pending";
};

export const getConfigValue = (config, key, row) => {
  const value = config?.[key];
  if (typeof value === "function") return value(row);
  if (Array.isArray(value)) return firstValue(row, value);
  if (typeof value === "string") return row[value];
  return value;
};

export const getDocumentNumber = (row, config) =>
  text(getConfigValue(config, "number", row) || firstValue(row, [
    "invoiceNumber",
    "billNumber",
    "billsNumber",
    "quoteNumber",
    "estimateNumber",
    "salesOrderNumber",
    "deliveryChallan",
    "purchaseNumber",
    "creditNoteNumber",
    "expenseNumber",
    "id",
  ]));

export const getPartyName = (row, config) =>
  text(getConfigValue(config, "party", row) || firstValue(row, ["customerName", "vendorName", "paidThrough", "payee", "expenseAccount", "name"], "Unknown"));

export const getDocumentDate = (row, config) =>
  text(getConfigValue(config, "date", row) || firstValue(row, [
    "invoiceDate",
    "quoteDate",
    "estimateDate",
    "salesOrderDate",
    "deliveryChallanDate",
    "billDate",
    "billsDate",
    "purchaseDate",
    "creditNoteDate",
    "date",
    "expenseDate",
  ]));

export const getDocumentAmount = (row, config) =>
  getConfigValue(config, "amount", row) || firstValue(row, ["total", "amount", "grandTotal", "creditAmount", "expenseAmount"], 0);

export const getDocumentStatus = (row, config) => {
  const rawStatus = getConfigValue(config, "status", row) || row.status || row.paymentStatus || "Draft";
  return {
    label: text(rawStatus, "Draft"),
    tone: statusTone(rawStatus),
  };
};

export const getRecordKey = (row, config, index) =>
  getDocumentNumber(row, config) || row.id || `${config?.documentType || "document"}-${index}`;

export const defaultDocumentConfigs = {
  quotation: {
    documentType: "quotation",
    title: "Quotations",
    singular: "Quotation",
    countLabel: "quotations",
    number: "quoteNumber",
    party: "customerName",
    date: "quoteDate",
    validLabel: "Valid Till",
    validValue: ["validTill", "expiryDate"],
    amount: "total",
    statusOptions: ["All", "Draft", "Sent", "Accepted", "Rejected", "Expired"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Convert to Invoice"],
  },
  estimate: {
    documentType: "estimate",
    title: "Estimates",
    singular: "Estimate",
    countLabel: "estimates",
    number: "estimateNumber",
    party: "customerName",
    date: "estimateDate",
    validLabel: "Expiry Date",
    validValue: "expiryDate",
    amount: "total",
    statusOptions: ["All", "Draft", "Sent", "Accepted", "Rejected", "Expired"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Convert to Invoice"],
  },
  purchase_order: {
    documentType: "purchase_order",
    title: "Purchase Orders",
    singular: "Purchase Order",
    countLabel: "purchase orders",
    number: "purchaseNumber",
    party: "vendorName",
    partyLabel: "Vendor",
    date: "purchaseDate",
    validLabel: "Expected Delivery",
    validValue: ["expectedDeliveryDate", "deliveryDate"],
    amount: "total",
    statusOptions: ["All", "Draft", "Sent", "Received", "Cancelled"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Convert to Purchase"],
  },
  bill: {
    documentType: "bill",
    title: "Bills",
    singular: "Bill",
    countLabel: "bills",
    number: ["billNumber", "billsNumber"],
    party: "vendorName",
    partyLabel: "Vendor",
    date: ["billsDate", "billDate", "date"],
    validLabel: "Due Date",
    validValue: ["dueDate", "billsDueDate"],
    amount: "total",
    statusOptions: ["All", "Draft", "Open", "Paid", "Overdue", "Cancelled"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Record Payment"],
  },
  sales_order: {
    documentType: "sales_order",
    title: "Sales Orders",
    singular: "Sales Order",
    countLabel: "sales orders",
    number: "salesOrderNumber",
    party: "customerName",
    date: "salesOrderDate",
    validLabel: "Delivery Date",
    validValue: "deliveryDate",
    amount: "total",
    statusOptions: ["All", "Draft", "Confirmed", "Packed", "Delivered", "Cancelled"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Convert to Invoice"],
  },
  delivery_challan: {
    documentType: "delivery_challan",
    title: "Delivery Challans",
    singular: "Delivery Challan",
    countLabel: "delivery challans",
    number: "deliveryChallan",
    party: "customerName",
    date: "deliveryChallanDate",
    validLabel: "Delivery Address",
    validValue: ["deliveryAddress", "shipAddress1", "billAddress1"],
    amount: ["total", "quantity"],
    statusOptions: ["All", "Draft", "Delivered", "Returned", "Converted"],
    actions: ["Edit", "Download PDF", "Print", "Convert to Invoice"],
  },
  pro_forma: {
    documentType: "pro_forma",
    title: "Pro Forma",
    singular: "Pro Forma Invoice",
    countLabel: "pro forma invoices",
    number: "invoiceNumber",
    party: "customerName",
    date: "invoiceDate",
    validLabel: "Valid Till",
    validValue: ["validTill", "dueDate"],
    amount: "total",
    statusOptions: ["All", "Draft", "Sent", "Accepted", "Converted"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Convert to Invoice"],
  },
  recurring_invoice: {
    documentType: "recurring_invoice",
    title: "Recurring Invoices",
    singular: "Recurring Profile",
    countLabel: "recurring profiles",
    number: "invoiceNumber",
    party: "customerName",
    date: "invoiceDate",
    validLabel: "Next Invoice",
    validValue: ["nextInvoiceDate", "dueDate"],
    amount: "total",
    statusOptions: ["All", "Active", "Paused", "Stopped"],
    actions: ["Edit", "Pause", "Resume", "Generate Invoice", "View History"],
  },
  credit_note: {
    documentType: "credit_note",
    title: "Credit Notes",
    singular: "Credit Note",
    countLabel: "credit notes",
    number: "creditNoteNumber",
    party: "customerName",
    date: "creditNoteDate",
    validLabel: "Invoice Reference",
    validValue: ["invoiceNumber", "reference"],
    amount: "total",
    statusOptions: ["All", "Open", "Applied", "Refunded", "Cancelled"],
    actions: ["Edit", "Send Mail", "Download PDF", "Print", "Apply to Invoice"],
  },
  expense: {
    documentType: "expense",
    title: "Expenses",
    singular: "Expense",
    countLabel: "expenses",
    number: ["expenseNumber", "invoice", "id"],
    party: ["vendorName", "paidThrough", "expenseAccount"],
    partyLabel: "Vendor / Payee",
    date: "date",
    validLabel: "Category",
    validValue: "expenseAccount",
    amount: "amount",
    statusOptions: ["All", "Paid", "Unpaid", "Reimbursable"],
    actions: ["Edit", "Upload Receipt", "Download PDF", "Print"],
  },
};
